import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';
import { api } from '@shared/api';

// The shared operation owns execution and replay; the browser supplies only the ticket identity.
export const aiReplyResponseSchema: z.ZodType<AiReplyResponse> = z.object({
  data: z.object({
    id: z.uuid(),
    useCase: z.literal('helpdesk_reply_draft'),
    state: z.literal('succeeded'),
    output: z.object({
      text: z
        .string()
        .min(1)
        .max(20000)
        .refine((text) => text.trim().length > 0 && !text.includes('\0')),
      basis: z.object({
        taskUuid: z.uuid(),
        publicThreadSha256: z.string().regex(/^[a-f0-9]{64}$/),
        publicReplyCount: z.number().int().nonnegative(),
        capturedAt: z.iso.datetime(),
        knowledge: z.object({
          mode: z.literal('aggregate'),
          activeSetSha256: z.string().regex(/^[a-f0-9]{64}$/),
        }),
      }),
      currentAtCompletion: z.boolean(),
      saved: z.literal(false),
    }),
  }),
});
export type ApplicationAiResponse =
  operations['postApiV1ApplicationAiResponses']['responses'][201]['content']['application/json'];
export type AiReplyResponse = {
  data: Extract<ApplicationAiResponse['data'], { useCase: 'helpdesk_reply_draft' }>;
};
// This synchronous operation includes knowledge search, generation and freshness checks.
// The shared 30-second read timeout can expire before even the configured model timeout.
const REPLY_DRAFT_REQUEST_TIMEOUT_MS = 180_000;
export function generateHelpdeskReply(taskUuid: string, key: string): Promise<AiReplyResponse> {
  return api.post(
    '/api/v1/application-ai-responses',
    {
      useCase: 'helpdesk_reply_draft',
      input: { taskUuid: z.uuid().parse(taskUuid) },
    },
    {
      headers: { 'Idempotency-Key': key },
      schema: aiReplyResponseSchema,
      skipAuthRefresh: true,
      timeout: REPLY_DRAFT_REQUEST_TIMEOUT_MS,
    }
  );
}
